import { HTTPError } from 'ky';
import { useQuery, useQueryClient } from 'react-query';

import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { GET_LEADS_PATH } from 'src/services/api/leads/constants';
import { getLeadFactsQueryKey } from 'src/services/api/leads/lead-facts';

export type FactExtractionRequest = {
    shouldCall: boolean;
    /** Newest triggering lead message uuid — the query key, so a run is spent only when
     *  new fact-relevant content arrives, not on every message. */
    triggerMessageUuid: string;
};

/**
 * Run a server-side LLM extraction pass over the conversation tail. The server is
 * idempotent on the newest message uuid and enforces the per-lead budget, so re-mounts
 * and duplicate fires are no-ops. The outcome is the status code (no body), mirroring
 * the create-suggestion endpoint: `201` a run happened, `200` no new messages, `429`
 * budget exhausted. New pending rows arrive via the invalidated suggestions query.
 */
export const useCreateFactExtraction = (leadUuid: string, request: FactExtractionRequest) => {
    const queryClient = useQueryClient();

    return useQuery(
        [queryKeys.factSuggestions, leadUuid, request.triggerMessageUuid],
        async () => {
            try {
                const res = await apiClient(`${GET_LEADS_PATH}/${leadUuid}/suggestions/extractions`, {
                    method: 'post',
                    json: {},
                    timeout: 15000, // gemini min timeout is 10s, we do a bit more than that
                });
                // 201 = ran (created/refreshed rows); 200 = no new messages
                return res.status === 201;
            } catch (error) {
                // 429 = per-lead budget exhausted: an expected no-op, not a failure
                if (error instanceof HTTPError && error.response.status === 429) {
                    return false;
                }
                throw error;
            }
        },
        {
            enabled: request.shouldCall,
            retry: false,
            staleTime: Infinity,
            onSuccess: (ran: boolean) => {
                if (ran) {
                    queryClient.invalidateQueries(getLeadFactsQueryKey(leadUuid));
                }
            },
        }
    );
};
